import { useMutation } from '@apollo/client/react';
import {
  Button,
  HStack,
  IconButton,
  Link,
  Menu,
  Popover,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronsDown,
  FiChevronsUp,
  FiMoreVertical,
  FiRefreshCw,
  FiRotateCcw,
} from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useDocumentContext } from './document-context';
import { editorDataHasLeadingFilesSection } from './editor-files-section';
import { parseEditorJs } from './editorjs-types';
import { AdvancedDocumentModal } from './modals/advanced-document-modal';
import { CreateSubpageModal } from './modals/create-subpage-modal';
import { RenameDocumentModal } from './modals/rename-document-modal';
import { refetchInTransition } from './refetch-utils';

import { PageHeaderPortal } from '#src/components/page-header-portal';
import { toaster } from '#src/components/ui/toaster';
import { UPSERT_DOCUMENT } from '#src/graphql/mutations/upsert-document';
import { useAppConfig } from '#src/providers/app-config-provider';
import { usePageContentSidebar } from '#src/providers/page-layout-provider';
import { formatDocumentName } from '#src/utils/format-document-name';

interface DocumentChildrenButtonProps {
  parentPath: string;
  items: Array<{ id: string; path: string }>;
  hasMore: boolean;
}

function DocumentChildrenButton({
  parentPath,
  items,
  hasMore,
}: DocumentChildrenButtonProps) {
  const countLabel = hasMore ? `${items.length}+` : `${items.length}`;

  return (
    <Popover.Root positioning={{ placement: 'bottom-start' }} size="sm">
      <Popover.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          color="fg.muted"
          fontWeight="normal"
          fontSize="sm"
          px={2}
          minW={0}
          title="Child documents"
        >
          🔗 {countLabel}
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="320px"
            maxH="300px"
            overflowY="auto"
            p={3}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={1} align="stretch">
              <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={1}>
                Child documents
              </Text>
              {items.map((child: { id: string; path: string }) => (
                <Link
                  key={child.id}
                  asChild
                  display="block"
                  fontSize="sm"
                  color="blue.600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  <RouterLink to={`/documents/${child.path}`}>
                    {formatDocumentName(
                      getRelativePath(parentPath, child.path),
                    ).join(' › ')}
                  </RouterLink>
                </Link>
              ))}
              {hasMore && (
                <Text fontSize="xs" color="fg.subtle" fontStyle="italic">
                  More than {items.length} children (showing first{' '}
                  {items.length})
                </Text>
              )}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

function getRelativePath(parentPath: string, childPath: string): string {
  if (!parentPath) return childPath;
  if (childPath === parentPath) return '';
  const prefix = parentPath + '/';
  return childPath.startsWith(prefix)
    ? childPath.slice(prefix.length)
    : childPath;
}

interface ViewDocumentHeaderProps {
  onNavigateToHistory: () => void;
  isCreateSubpageOpen: boolean;
  onCreateSubpageOpenChange: (open: boolean) => void;
  /** Matches document editor mobile breakpoint (e.g. 650px). */
  isMobileEditorLayout: boolean;
  composerFooterVisible: boolean;
  onComposerFooterToggle: () => void;
}

export function ViewDocumentHeader({
  onNavigateToHistory,
  isCreateSubpageOpen,
  onCreateSubpageOpenChange,
  isMobileEditorLayout,
  composerFooterVisible,
  onComposerFooterToggle,
}: ViewDocumentHeaderProps) {
  const navigate = useNavigate();
  const {
    document,
    displayPath,
    documentId,
    isNewDocument,
    childrenItems,
    childrenHasMore,
    isDirty,
    dirtyEditGeneration,
    getSerializedDocument,
    save,
    saving,
    saveInFlightRef,
    revertToSaved,
    refetch,
  } = useDocumentContext();
  const {
    removeRecentlyOpenedDocumentById,
    config,
    setDocumentAutoSaveEnabled,
  } = useAppConfig();
  const {
    contentSidebarSlotActive,
    contentSidebarDismissed,
    setContentSidebarDismissed,
  } = usePageContentSidebar();
  const { documentAutoSaveEnabled: autoSaveEnabled } = config;
  const [upsertDocument] = useMutation(UPSERT_DOCUMENT);
  const isDirtyRef = useRef(isDirty);
  const autoSaveEnabledRef = useRef(autoSaveEnabled);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const saveRef = useRef<(options?: { silent?: boolean }) => Promise<void>>(
    () => Promise.resolve(),
  );

  const hasChildren = childrenItems.length > 0;

  /** Stable for memo deps — `childrenItems` array identity can churn without list changes. */
  const childrenItemsSyncKey = useMemo(
    () => childrenItems.map((c) => `${c.id}:${c.path}`).join(','),
    [childrenItems],
  );

  const showDocumentFilesLink = useMemo(() => {
    void dirtyEditGeneration;
    if (document?.__typename !== 'Document' || isNewDocument) return false;
    try {
      return editorDataHasLeadingFilesSection(
        parseEditorJs(getSerializedDocument()) ?? null,
      );
    } catch {
      return false;
    }
  }, [document, dirtyEditGeneration, getSerializedDocument, isNewDocument]);

  const documentPathForRename =
    document?.__typename === 'Document' ? document.path : displayPath;

  const handleRename = useCallback(
    async (newPath: string, currentValue: string) => {
      if (document?.__typename !== 'Document') return;
      try {
        await upsertDocument({
          variables: {
            input: {
              id: document.id,
              path: newPath,
              value: currentValue,
            },
          },
        });
        toaster.create({
          title: 'Success',
          description: 'Document renamed.',
        });
        removeRecentlyOpenedDocumentById(document.id);
        await refetchInTransition(refetch);
        navigate(`/documents/${newPath}`, { replace: true });
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    },
    [
      document,
      navigate,
      refetch,
      removeRecentlyOpenedDocumentById,
      upsertDocument,
    ],
  );

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    autoSaveEnabledRef.current = autoSaveEnabled;
  }, [autoSaveEnabled]);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  useEffect(() => {
    if (!autoSaveEnabled || !isDirty) return;
    const id = window.setTimeout(() => {
      if (
        !autoSaveEnabledRef.current ||
        !isDirtyRef.current ||
        saveInFlightRef.current
      ) {
        return;
      }
      void saveRef.current({ silent: true });
    }, 5000);
    return () => window.clearTimeout(id);
  }, [autoSaveEnabled, dirtyEditGeneration, isDirty, saveInFlightRef]);

  const autoSaveToggleButton = useMemo(
    () => (
      <IconButton
        aria-label={
          autoSaveEnabled
            ? 'Disable auto-save'
            : 'Enable auto-save (5 seconds after you stop editing)'
        }
        variant={autoSaveEnabled ? 'solid' : 'ghost'}
        colorPalette={autoSaveEnabled ? 'blue' : 'gray'}
        size="sm"
        title={
          autoSaveEnabled
            ? 'Disable auto-save'
            : 'Enable auto-save (5 seconds after you stop editing)'
        }
        loading={saving}
        disabled={saving}
        onClick={() => setDocumentAutoSaveEnabled(!autoSaveEnabled)}
      >
        <FiRefreshCw />
      </IconButton>
    ),
    [autoSaveEnabled, saving, setDocumentAutoSaveEnabled],
  );

  const revertButton = useMemo(
    () => (
      <IconButton
        aria-label="Revert to last saved"
        variant="ghost"
        size="sm"
        title="Revert to last saved version"
        onClick={revertToSaved}
      >
        <FiRotateCcw />
      </IconButton>
    ),
    [revertToSaved],
  );

  const actionsContent = !isNewDocument ? (
    isDirty ? (
      <HStack gap={2}>
        {revertButton}
        {autoSaveEnabled ? (
          autoSaveToggleButton
        ) : (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => void save()}
            loading={saving}
            loadingText="Saving..."
          >
            Save
          </Button>
        )}
      </HStack>
    ) : (
      autoSaveToggleButton
    )
  ) : null;

  const titleContent = useMemo(() => {
    const rightControls = (
      <HStack gap={2} align="center">
        {hasChildren && (
          <DocumentChildrenButton
            parentPath={displayPath}
            items={childrenItems}
            hasMore={childrenHasMore}
          />
        )}
        {showDocumentFilesLink && documentId && (
          <Link
            asChild
            fontSize="sm"
            color="blue.600"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
          >
            <RouterLink to={`/documents/${displayPath}/files`}>
              Files
            </RouterLink>
          </Link>
        )}
        {(isMobileEditorLayout ||
          (contentSidebarSlotActive && contentSidebarDismissed)) && (
          <IconButton
            aria-label={
              isMobileEditorLayout
                ? composerFooterVisible
                  ? 'Hide AI sidebar'
                  : 'Show AI sidebar'
                : 'Show AI sidebar'
            }
            variant="ghost"
            size="sm"
            title={
              isMobileEditorLayout
                ? composerFooterVisible
                  ? 'Hide AI sidebar'
                  : 'Show AI sidebar'
                : 'Show AI sidebar'
            }
            onClick={() => {
              if (isMobileEditorLayout) {
                onComposerFooterToggle();
              } else {
                setContentSidebarDismissed(false);
              }
            }}
          >
            {isMobileEditorLayout ? (
              composerFooterVisible ? (
                <FiChevronsDown />
              ) : (
                <FiChevronsUp />
              )
            ) : (
              <FiChevronsUp />
            )}
          </IconButton>
        )}
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <IconButton aria-label="Document actions" variant="ghost" size="sm">
              <FiMoreVertical />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                {documentId && (
                  <Menu.Item
                    value="files"
                    onClick={() => navigate(`/documents/${displayPath}/files`)}
                  >
                    Files
                  </Menu.Item>
                )}
                {!isNewDocument && (
                  <>
                    <Menu.Item
                      value="rename"
                      onClick={() => setIsRenameOpen(true)}
                    >
                      Rename
                    </Menu.Item>
                    <Menu.Item value="history" onClick={onNavigateToHistory}>
                      History
                    </Menu.Item>
                    {documentId && (
                      <Menu.Item
                        value="advanced"
                        onClick={() => setIsAdvancedModalOpen(true)}
                      >
                        Advanced
                      </Menu.Item>
                    )}
                  </>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
        {isNewDocument && (
          <HStack gap={2}>
            {isDirty && revertButton}
            {(autoSaveEnabled || !isDirty) && autoSaveToggleButton}
            {isDirty && !autoSaveEnabled && (
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => void save()}
                loading={saving}
                loadingText="Saving..."
              >
                Save
              </Button>
            )}
          </HStack>
        )}
      </HStack>
    );

    return (
      <HStack gap={4} align="center" justify="flex-end" width="100%" minW={0}>
        {rightControls}
      </HStack>
    );
    // `childrenItems` is covered by `childrenItemsSyncKey` (ids + paths).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [
    childrenItemsSyncKey,
    childrenHasMore,
    displayPath,
    navigate,
    documentId,
    hasChildren,
    isNewDocument,
    isMobileEditorLayout,
    composerFooterVisible,
    onComposerFooterToggle,
    contentSidebarSlotActive,
    contentSidebarDismissed,
    setContentSidebarDismissed,
    showDocumentFilesLink,
    onNavigateToHistory,
    save,
    isDirty,
    saving,
    autoSaveEnabled,
    autoSaveToggleButton,
    revertButton,
  ]);

  const pageHeaderTitleSyncKey = useMemo(
    () =>
      [
        displayPath,
        documentId ?? '',
        childrenItemsSyncKey,
        String(childrenHasMore),
        String(showDocumentFilesLink),
        String(hasChildren),
        String(isNewDocument),
        String(isMobileEditorLayout),
        String(composerFooterVisible),
        String(contentSidebarSlotActive),
        String(contentSidebarDismissed),
        String(isDirty),
        String(saving),
        String(autoSaveEnabled),
      ].join('\u001f'),
    [
      displayPath,
      documentId,
      childrenItemsSyncKey,
      childrenHasMore,
      showDocumentFilesLink,
      hasChildren,
      isNewDocument,
      isMobileEditorLayout,
      composerFooterVisible,
      contentSidebarSlotActive,
      contentSidebarDismissed,
      isDirty,
      saving,
      autoSaveEnabled,
    ],
  );

  return (
    <>
      <PageHeaderPortal
        title={titleContent}
        titleSyncKey={pageHeaderTitleSyncKey}
      >
        {actionsContent}
      </PageHeaderPortal>

      {!isNewDocument && (
        <RenameDocumentModal
          open={isRenameOpen}
          onOpenChange={setIsRenameOpen}
          documentPathForRename={documentPathForRename}
          getCurrentValue={getSerializedDocument}
          onRename={handleRename}
        />
      )}

      <AdvancedDocumentModal
        open={isAdvancedModalOpen}
        onOpenChange={setIsAdvancedModalOpen}
        documentId={documentId}
        getCurrentValue={getSerializedDocument}
      />

      <CreateSubpageModal
        open={isCreateSubpageOpen}
        onOpenChange={onCreateSubpageOpenChange}
        displayPath={displayPath}
      />
    </>
  );
}
